import { useReducer } from 'react';
import useBusinessIdExists from 'hooks/useBusinessIdExists';
import { validateStep } from 'utils/formValidation';
import useSubmit from 'hooks/useSubmit';
import Project from 'models/Project';
import createProjectFormReducer, {
  UNSET_FIELD_ERROR,
  SET_FIELD,
  SET_FIELD_ERROR,
  SET_ERRORS,
  INCREASE_STEP,
} from 'reducers/createProjectFormReducer';
import { getErrorMessage } from 'utils/errorMessages';
import { steps } from 'utils/steps';

const defaultInitialState = {
  data: {
    firstname: '',
    insertion: '',
    lastname: '',
    email: '',
    countryCode: '+31',
    mobilePhone: '',
    streetname: '',
    housenumber: '',
    zipcode: '',
    city: 'Zeewolde',
    title: '',
    description: '',
    images: [],
  },
  errors: {},
  currentStep: 0,
  loading: false,
};

// This hook will contain form logic for create project form.
const useCreateProjectForm = ({
  businessId,
  initialState = defaultInitialState,
}) => {
  const { loading: businessIdExistsLoading } = useBusinessIdExists({
    businessId,
  });

  const [state, dispatch] = useReducer(createProjectFormReducer, initialState);

  const { handleSubmitButtonClick } = useSubmit({
    state,
    dispatch,
    businessId,
  });

  const loading = state.loading || businessIdExistsLoading;

  const handleChange = ({ target: { value, name: fieldName } }) => {
    if (state.errors[fieldName]) {
      // Reset error for this input.
      dispatch({ type: UNSET_FIELD_ERROR, payload: { fieldName } });
    }

    // Update value for this input.
    dispatch({ type: SET_FIELD, payload: { fieldName, value } });
  };

  const handleFileInputChange = ({ target: { files } }) => {
    const errorMessageKey = Project.validateField('images', files);

    if (errorMessageKey) {
      return dispatch({
        type: SET_FIELD_ERROR,
        payload: {
          fieldName: 'images',
          errorMessage: getErrorMessage(errorMessageKey),
        },
      });
    }

    if (state.errors.images) {
      dispatch({ type: UNSET_FIELD_ERROR, payload: { fieldName: 'images' } });
    }

    dispatch({
      type: SET_FIELD,
      payload: { fieldName: 'images', value: files },
    });
  };

  const handleBlur = ({ target: { value, name: fieldName } }) => {
    // Validate input
    const errorMessageKey = Project.validateField(fieldName, value);
    if (!errorMessageKey) return;

    // Input has an error, show message below input.
    dispatch({
      type: SET_FIELD_ERROR,
      payload: { fieldName, errorMessage: getErrorMessage(errorMessageKey) },
    });
  };

  const handleNextStepButtonClick = () => {
    // Validate the fields in the current step.
    const validationResponse = validateStep({
      fields: steps[state.currentStep].fields,
      data: state.data,
    });

    // Show errors messages under input.
    if (validationResponse) {
      return dispatch({
        type: SET_ERRORS,
        payload: { errors: validationResponse },
      });
    }

    // Go to next step if it exists.
    if (steps[state.currentStep + 1]) {
      return dispatch({ type: INCREASE_STEP });
    }
  };

  return {
    data: state.data,
    errors: state.errors,
    images: state.images,
    currentStep: state.currentStep,
    loading,
    handleChange,
    handleFileInputChange,
    handleBlur,
    handleNextStepButtonClick,
    handleSubmitButtonClick,
  };
};

export default useCreateProjectForm;
